import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Database, Download, Loader2, Search, History, CheckCircle2, AlertCircle } from "lucide-react";
import { Input, PageHeader } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

/**
 * Super Admin master data — seed platform-wide defaults that every tenant inherits.
 * Mirrors the Settings page on the firm side but writes to the `scope=global` set.
 */
const KINDS = [
    // ── Cases ──
    { kind: "case_categories",   label: "Case categories" },
    { kind: "case_types",        label: "Case types" },
    { kind: "case_stages",       label: "Case status" },
    // ── Courts (eCourts docs split into 4) ──
    { kind: "courts",            label: "Courts" },
    { kind: "high_courts",       label: "High courts" },
    { kind: "court_types",       label: "Court types" },
    { kind: "benches",           label: "Bench types" },
    // ── Specialised tribunals (derived from courtCode) ──
    { kind: "nclt_benches",      label: "NCLT benches" },
    { kind: "nclat_benches",     label: "NCLAT benches" },
    // ── Geography & sections ──
    { kind: "states",            label: "States & UTs" },
    { kind: "judicial_sections", label: "Judicial sections" },
    { kind: "cause_list_types",  label: "Cause list types" },
    // ── Legacy / firm-side ──
    { kind: "hearing_types",     label: "Hearing types" },
    { kind: "fee_types",         label: "Fee types" },
    { kind: "expense_types",     label: "Expense types" },
];

function GlobalConfigGroup({ kind, label }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [name, setName] = useState("");
    const [q, setQ] = useState("");
    const debounceRef = useRef(null);

    const load = useCallback(async (search = "") => {
        try {
            const r = await api.get(`/platform/config/${kind}`, {
                params: { q: search || undefined, limit: 100 },
            });
            setItems(r.data || []);
            setTotal(parseInt(r.headers?.["x-total-count"] || r.data?.length || 0, 10));
        } catch (e) { toast.error(apiError(e)); }
    }, [kind]);

    // Debounced server-side search (also runs on mount since q starts as "")
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => load(q.trim()), 250);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [q, load]);

    const add = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try { await api.post(`/platform/config/${kind}`, { name }); setName(""); load(q.trim()); toast.success(`${label}: added to platform defaults`); }
        catch (e) { toast.error(apiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm(`Remove this ${label.toLowerCase()} from platform defaults?`)) return;
        try { await api.delete(`/platform/config/${kind}/${id}`); load(q.trim()); toast.success("Removed"); }
        catch (e) { toast.error(apiError(e)); }
    };

    const showing = items.length;
    const hasOverflow = total > showing;

    return (
        <div className="bg-muted border border-border p-6 flex flex-col h-[480px]" data-testid={`platform-config-card-${kind}`}>
            <div className="flex items-center justify-between mb-4 shrink-0">
                <div className="overline">{label}</div>
                <div className="text-[10px] text-muted-foreground font-mono tracking-wider" data-testid={`platform-config-count-${kind}`}>
                    {showing}{hasOverflow ? ` of ${total.toLocaleString()}` : ""}
                </div>
            </div>
            <form onSubmit={add} className="flex gap-2 mb-2 shrink-0">
                <Input value={name} onChange={e => setName(e.target.value)} placeholder={`Add ${label.toLowerCase()}…`} data-testid={`platform-config-input-${kind}`}/>
                <button data-testid={`platform-config-add-${kind}`} className="bg-primary text-primary-foreground hover:bg-primary px-3 transition-colors"><Plus className="w-4 h-4" strokeWidth={2}/></button>
            </form>
            {(total > 20 || q) && (
                <div className="relative mb-3 shrink-0">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2"/>
                    <input
                        value={q} onChange={e => setQ(e.target.value)}
                        placeholder={`Search ${total.toLocaleString()} ${label.toLowerCase()}…`}
                        data-testid={`platform-config-search-${kind}`}
                        className="w-full bg-background border border-border focus:border-primary outline-none pl-7 pr-3 py-1.5 text-xs"/>
                </div>
            )}
            <div
                className="flex flex-wrap gap-2 overflow-y-auto pr-1 flex-1 min-h-0 master-data-scroll content-start"
                data-testid={`platform-config-list-${kind}`}
            >
                {items.length === 0 ? (
                    <div className="text-[10px] text-muted-foreground">{q ? "No matches." : `No platform defaults yet — every firm will start with an empty ${label.toLowerCase()} list.`}</div>
                ) : items.map(it => (
                    <div key={it.id} className="group inline-flex items-center gap-2 border border-border hover:border-primary px-3 py-1.5 text-xs h-fit" title={it.source === "ecourts" ? "Synced from eCourts" : ""}>
                        <span>{it.name}</span>
                        {it.source === "ecourts" && <span className="text-[8px] text-primary/60 uppercase tracking-wider">eC</span>}
                        <button onClick={() => remove(it.id)} data-testid={`platform-config-remove-${it.id}`} className="text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100">
                            <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                        </button>
                    </div>
                ))}
            </div>
            {hasOverflow && !q && (
                <div className="mt-3 text-[10px] text-muted-foreground shrink-0 pt-2 border-t border-border/50">
                    Showing {showing} of {total.toLocaleString()} · use search above to find specific items.
                </div>
            )}
        </div>
    );
}

export default function AdminMasterData() {
    const [syncing, setSyncing] = useState(false);
    const [lastSync, setLastSync] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const syncEcourts = async () => {
        if (!window.confirm("Pull authoritative court / case-type / status enums from eCourts and merge into platform master data?\n\nIdempotent — only new items are inserted; existing items are left untouched.")) return;
        setSyncing(true);
        try {
            const { data } = await api.post("/platform/master-data/sync-ecourts", null, {
                timeout: 5 * 60 * 1000,   // 5 min — sync touches 10k+ rows + remote vendor API
            });
            const total = Object.values(data.summary || {}).reduce((s, x) => s + (x?.added || 0), 0);
            toast.success(`Synced from eCourts · ${total} new items added`);
            setLastSync(data);
            setReloadKey(k => k + 1);
        } catch (e) {
            // Show the precise reason so production environment issues
            // (missing eCourts token, vendor downtime, firewall) are debuggable.
            const detail = e?.response?.data?.detail || apiError(e);
            toast.error(`eCourts sync failed · ${detail}`, { duration: 10000 });
        } finally {
            setSyncing(false);
        }
    };
    return (
        <>
            <PageHeader
                title="Master data"
                subtitle="Platform-wide pickers. Every firm sees these as default options in their forms and can add their own on top — but cannot remove these defaults."
            />
            <div className="bg-muted border border-primary/30 p-4 mb-6 flex items-start gap-3" data-testid="master-data-ecourts-banner">
                <Database className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5}/>
                <div className="flex-1">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        Each item you add here propagates instantly to all tenants in their <span className="text-foreground">Litigations</span>, <span className="text-foreground">Hearings</span>, <span className="text-foreground">Invoices</span> and <span className="text-foreground">Settings</span> pickers.
                        Tenants can extend with firm-specific values but cannot remove platform defaults.
                    </p>
                    {lastSync && (
                        <div className="mt-2 text-[11px] text-emerald-400 font-mono">
                            Last sync: {Object.entries(lastSync.summary || {}).map(([k, v]) => `${k}+${v.added}`).join(" · ") || "no new items"}
                        </div>
                    )}
                </div>
                <button
                    onClick={syncEcourts}
                    disabled={syncing}
                    data-testid="master-data-sync-ecourts"
                    className="border border-primary text-primary hover:bg-primary/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                    {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Download className="w-3.5 h-3.5"/>}
                    {syncing ? "Syncing…" : "Sync from eCourts"}
                </button>
            </div>
            <SyncAuditDrawer/>
            <div className="grid lg:grid-cols-2 gap-6" key={reloadKey}>
                {KINDS.map(k => <GlobalConfigGroup key={k.kind} {...k}/>)}
            </div>
        </>
    );
}

function SyncAuditDrawer() {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ecourts-sync-audit?limit=30");
            setRows(data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { if (open) load(); }, [open, load]);
    const ctrl = useTableControls(rows, {
        accessors: {
            when: (r) => r.started_at,
            status: (r) => r.error ? "failed" : "ok",
            outcome: (r) => r.error ? r.error : Object.entries(r.summary || {}).map(([k, v]) => `${k}+${v?.added || 0}`).join(" "),
        },
    });
    return (
        <div className="mb-6">
            <button onClick={() => setOpen(!open)} data-testid="ecourts-sync-audit-toggle"
                className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-primary inline-flex items-center gap-1.5">
                <History className="w-3 h-3"/>{open ? "Hide" : "Show"} sync history ({rows.length || "—"})
            </button>
            {open && (
                <div className="mt-3 border border-border bg-card overflow-x-auto">
                    <FilterChip ctrl={ctrl} className="m-2"/>
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            <tr className="border-b border-border">
                                <SortableTh field="when" ctrl={ctrl} className="text-left px-3 py-2">When</SortableTh>
                                <SortableTh field="trigger" ctrl={ctrl} className="text-left px-3 py-2">Trigger</SortableTh>
                                <SortableTh field="status" ctrl={ctrl} className="text-center px-3 py-2" align="center">Status</SortableTh>
                                <SortableTh field="duration_ms" ctrl={ctrl} className="text-right px-3 py-2" align="right">Duration</SortableTh>
                                <SortableTh field="outcome" ctrl={ctrl} className="text-left px-3 py-2">Outcome</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {ctrl.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No sync history yet.</td></tr>}
                            {ctrl.rows.map(r => {
                                const total = Object.values(r.summary || {}).reduce((s, v) => s + (v?.added || 0), 0);
                                const ok = !r.error && r.summary;
                                return (
                                    <tr key={r.id} className="border-b border-card" data-testid={`ecourts-audit-row-${r.id}`}>
                                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.started_at ? fmtDateTime(r.started_at) : "—"}</td>
                                        <td className="px-3 py-2 text-muted-foreground text-[10px] uppercase tracking-wider">{r.trigger}</td>
                                        <td className="px-3 py-2 text-center">{ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline"/> : <AlertCircle className="w-3.5 h-3.5 text-rose-400 inline"/>}</td>
                                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">{r.duration_ms ? `${r.duration_ms}ms` : "—"}</td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {r.error ? <span className="text-rose-400">{r.error}</span> : <span className="text-emerald-400">+{total} new across {Object.keys(r.summary || {}).length} kinds</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

